import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminShell } from "@/components/AdminSidebar";
import { toast } from "sonner";
import { Check, MessageSquare, X, Settings, Eye, ZoomIn, GitCompare } from "lucide-react";

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const src = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);
const FILTERS = [["pending_review", "Pending"], ["changes_requested", "Changes"], ["active", "Active"], ["rejected", "Rejected"], ["all", "All"]];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const SIZES = [
  { key: "single", label: "X1", w: 324, h: 77, note: "In-race strip" },
  { key: "double", label: "X2", w: 324, h: 132, note: "Lobby banner" },
  { key: "triple", label: "X3", w: 324, h: 209, note: "Calendar tile" },
];
const REASON_PRESETS = [
  "The text is too small to read at the displayed ad size.",
  "The image is blurry or low resolution.",
  "The creative is stretched or cropped and doesn't fit the banner size.",
  "Low contrast — the message is hard to read.",
  "The ad contains prohibited, misleading, or off-brand content.",
  "The click-through URL is broken or doesn't match the ad.",
  "Please provide all three banner sizes for the best placement fit.",
];

export default function AdvertiserReview() {
  const [filter, setFilter] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [zoom, setZoom] = useState(2);
  const [reason, setReason] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [counts, setCounts] = useState({});
  const load = () => api.get(`/adv/admin/submissions?status=${filter}`).then((r) => setRows(r.data)).catch(() => {});
  const loadCounts = () => api.get("/adv/admin/counts").then((r) => setCounts(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    loadCounts();
    const h = () => loadCounts();
    window.addEventListener("adv-submissions-changed", h);
    return () => window.removeEventListener("adv-submissions-changed", h);
  }, []);
  useEffect(() => { api.get("/adv/admin/billing-settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); loadCounts(); window.dispatchEvent(new Event("adv-submissions-changed")); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const approve = (c) => act(() => api.post(`/adv/admin/campaigns/${c.id}/approve`), "Approved");
  const openReason = (c, mode) => { setPreview(null); setReasonText(""); setReason({ c, mode }); };
  const submitReason = () => {
    const t = reasonText.trim();
    if (!t) return toast.error("Add a short reason for the advertiser.");
    const { c, mode } = reason;
    const ep = mode === "changes" ? "request-changes" : "reject";
    act(() => api.post(`/adv/admin/campaigns/${c.id}/${ep}`, { message: t }), mode === "changes" ? "Changes requested" : "Rejected");
    setReason(null);
  };
  const saveSettings = async () => { try { await api.put("/adv/admin/billing-settings", settings); toast.success("Billing policy saved"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const runBilling = async () => { try { const { data } = await api.post("/adv/admin/run-billing"); toast.success(`Billing run: charged ${data.charged} advertiser(s)`); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <AdminShell>
      <h1 className="font-heading text-3xl tracking-tight mb-1" data-testid="admin-adv-title">Advertiser Submissions</h1>
      <p className="text-race-sub text-sm mb-5">Review and approve campaigns submitted by advertisers.</p>

      {settings && (
        <div className="bg-race-surface border border-race-line rounded-lg p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="admin-billing-settings">
          <div className="flex items-center gap-2 text-sm font-semibold text-race-txt mr-2"><Settings size={15} /> Weekly Billing Policy</div>
          <div><div className="text-[11px] uppercase text-race-muted mb-1">Weekly billing day</div>
            <select value={settings.weekly_billing_day} onChange={(e) => setSettings({ ...settings, weekly_billing_day: e.target.value })} data-testid="admin-billing-day" className="bg-race-bg border border-race-line rounded-md px-3 py-2 text-sm capitalize">
              {DAYS.map((d) => <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>)}
            </select></div>
          <div><div className="text-[11px] uppercase text-race-muted mb-1">Billing threshold ($)</div>
            <input type="number" value={settings.threshold_usd} onChange={(e) => setSettings({ ...settings, threshold_usd: Number(e.target.value) })} data-testid="admin-billing-threshold" className="bg-race-bg border border-race-line rounded-md px-3 py-2 text-sm w-28" /></div>
          <button onClick={saveSettings} data-testid="admin-billing-save" className="bg-race-txt text-race-bg font-semibold text-sm px-4 py-2 rounded-md">Save</button>
          <button onClick={runBilling} data-testid="admin-run-billing" className="border border-race-line text-sm px-4 py-2 rounded-md hover:border-race-volt ml-auto">Run billing now</button>
        </div>
      )}

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {FILTERS.map(([k, l]) => {
          const n = counts[k];
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-adv-filter-${k}`}
              className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${filter === k ? "bg-race-red text-race-bg font-semibold" : "text-race-sub border border-race-line hover:text-race-txt"}`}>
              {l}
              {n > 0 && <span data-testid={`admin-adv-filter-count-${k}`} className={`min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${filter === k ? "bg-race-bg/25 text-race-bg" : k === "pending_review" ? "bg-amber-400 text-race-bg" : "bg-race-line/60 text-race-txt"}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-race-line py-16 text-center text-race-sub" data-testid="admin-adv-empty">No campaigns in this view.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-adv-list">
          {rows.map((c) => (
            <div key={c.id} className="bg-race-surface border border-race-line rounded-lg p-4" data-testid={`admin-adv-row-${c.id}`}>
              <div className="flex items-start gap-4">
                <button onClick={() => { setZoom(2); setPreview(c); }} data-testid={`admin-adv-preview-${c.id}`}
                  className="w-32 h-16 rounded bg-race-bg border border-race-line overflow-hidden shrink-0 relative group cursor-zoom-in" title="Preview all sizes">
                  {c.image_url && <img src={src(c.image_url)} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 grid place-items-center transition-opacity"><ZoomIn size={16} className="text-white" /></span>
                </button>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-race-sub">{c.advertiser?.business_name} · {c.advertiser?.email} {!c.advertiser?.has_payment_method && <span className="text-race-red">· no payment method</span>}</div>
                  <div className="text-xs text-race-sub mt-1">Budget {money(c.budget_usd)} · CPM {money(c.cpm_usd)} · {c.start_date?.slice(0, 10)} → {c.end_date?.slice(0, 10)}</div>
                  <div className="text-xs text-race-muted mt-1">Audience: {c.interests?.length ? c.interests.join(", ") : "General"} · → {c.click_url}</div>
                  <button onClick={() => { setZoom(2); setPreview(c); }} data-testid={`admin-adv-preview-btn-${c.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-race-volt hover:underline"><Eye size={13} /> Preview all 3 sizes</button>
                </div>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-race-line/40 text-race-sub uppercase shrink-0">{c.status_label}</span>
              </div>
              <ResubmitDiff c={c} />
              {c.status === "pending_review" && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => approve(c)} data-testid={`admin-approve-${c.id}`} className="inline-flex items-center gap-1 bg-race-volt text-race-bg font-semibold text-xs px-3 py-1.5 rounded-md"><Check size={13} /> Approve</button>
                  <button onClick={() => openReason(c, "changes")} data-testid={`admin-changes-${c.id}`} className="inline-flex items-center gap-1 border border-race-line text-xs px-3 py-1.5 rounded-md hover:border-amber-400 hover:text-amber-300"><MessageSquare size={13} /> Request Changes</button>
                  <button onClick={() => openReason(c, "reject")} data-testid={`admin-reject-${c.id}`} className="inline-flex items-center gap-1 border border-race-line text-xs px-3 py-1.5 rounded-md hover:border-race-red hover:text-race-red"><X size={13} /> Reject</button>
                </div>
              )}
              {c.review_message && <div className="mt-2 text-xs text-race-muted">Last message: {c.review_message}</div>}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-start justify-center overflow-y-auto p-6" onClick={() => setPreview(null)} data-testid="admin-adv-preview-modal">
          <div className="bg-race-surface border border-race-line rounded-xl max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-5 border-b border-race-line">
              <div className="min-w-0">
                <div className="font-heading text-2xl tracking-tight">{preview.name}</div>
                <div className="text-xs text-race-sub mt-0.5">{preview.advertiser?.business_name} · Destination → <span className="text-race-txt">{preview.click_url}</span></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex rounded-md border border-race-line overflow-hidden text-xs">
                  {[1, 2].map((z) => <button key={z} onClick={() => setZoom(z)} data-testid={`admin-adv-zoom-${z}`} className={`px-2.5 py-1 ${zoom === z ? "bg-race-txt text-race-bg font-semibold" : "text-race-sub"}`}>{z}×</button>)}
                </div>
                <button onClick={() => setPreview(null)} data-testid="admin-adv-preview-close" className="w-8 h-8 grid place-items-center rounded-md border border-race-line text-race-sub hover:text-race-txt"><X size={16} /></button>
              </div>
            </div>
            <p className="text-xs text-race-muted px-5 pt-4">Shown at real placement size ({zoom}×). If any text isn't legible here, it won't be legible to racers — request changes.</p>
            <div className="px-5 pt-3"><ResubmitDiff c={preview} /></div>
            <div className="p-5 pt-3 space-y-5">
              {SIZES.map((z) => {
                const url = preview.creatives?.[z.key] || (z.key === "double" ? preview.image_url : "");
                return (
                  <div key={z.key} data-testid={`admin-adv-preview-${z.key}`}>
                    <div className="text-xs font-semibold text-race-txt mb-1.5">{z.label} <span className="text-race-muted font-normal">· {z.w}×{z.h} · {z.note}</span></div>
                    {url ? (
                      <div className="inline-block rounded-md border border-race-line overflow-hidden bg-race-bg">
                        <img src={src(url)} alt={z.label} style={{ width: z.w * zoom, height: z.h * zoom }} className="block object-cover max-w-full" />
                      </div>
                    ) : (
                      <div className="text-xs text-race-muted italic border border-dashed border-race-line rounded-md px-3 py-4">Not provided — will auto-fit from another size when served.</div>
                    )}
                  </div>
                );
              })}
            </div>
            {preview.status === "pending_review" && (
              <div className="flex gap-2 p-5 border-t border-race-line">
                <button onClick={() => { approve(preview); setPreview(null); }} data-testid="admin-adv-preview-approve" className="inline-flex items-center gap-1 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2 rounded-md"><Check size={14} /> Approve</button>
                <button onClick={() => openReason(preview, "changes")} className="inline-flex items-center gap-1 border border-race-line text-sm px-4 py-2 rounded-md hover:border-amber-400 hover:text-amber-300"><MessageSquare size={14} /> Request Changes</button>
                <button onClick={() => openReason(preview, "reject")} className="inline-flex items-center gap-1 border border-race-line text-sm px-4 py-2 rounded-md hover:border-race-red hover:text-race-red"><X size={14} /> Reject</button>
              </div>
            )}
          </div>
        </div>
      )}

      {reason && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-start justify-center overflow-y-auto p-6" onClick={() => setReason(null)} data-testid="admin-reason-modal">
          <div className="bg-race-surface border border-race-line rounded-xl max-w-lg w-full my-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 p-5 border-b border-race-line">
              <div className="font-heading text-xl tracking-tight">{reason.mode === "changes" ? "Request changes" : "Reject campaign"}</div>
              <button onClick={() => setReason(null)} data-testid="admin-reason-close" className="w-8 h-8 grid place-items-center rounded-md border border-race-line text-race-sub hover:text-race-txt"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-race-sub">"{reason.c.name}" — {reason.c.advertiser?.business_name}. Pick a common reason or write your own; the advertiser will see this message.</div>
              <div className="flex flex-wrap gap-2" data-testid="admin-reason-presets">
                {REASON_PRESETS.map((p, i) => (
                  <button key={i} onClick={() => setReasonText((t) => (t.trim() ? t.trim() + " " : "") + p)} data-testid={`admin-reason-preset-${i}`}
                    className="text-left text-xs px-2.5 py-1.5 rounded-md border border-race-line text-race-sub hover:border-race-volt hover:text-race-txt transition-colors">{p}</button>
                ))}
              </div>
              <textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} rows={4} data-testid="admin-reason-text"
                placeholder="Message to the advertiser…" className="w-full bg-race-bg border border-race-line rounded-md px-3 py-2 text-sm text-race-txt outline-none focus:border-race-volt resize-none" />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-race-line">
              <button onClick={() => setReason(null)} className="text-sm px-4 py-2 rounded-md border border-race-line text-race-sub hover:text-race-txt">Cancel</button>
              <button onClick={submitReason} data-testid="admin-reason-submit"
                className={`inline-flex items-center gap-1 font-semibold text-sm px-4 py-2 rounded-md ${reason.mode === "changes" ? "bg-amber-400 text-race-bg" : "bg-race-red text-race-bg"}`}>
                {reason.mode === "changes" ? <><MessageSquare size={14} /> Send request</> : <><X size={14} /> Reject campaign</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function ResubmitDiff({ c }) {
  if (!c?.resubmitted || !(c.resubmit_changes || []).length) return null;
  return (
    <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid={`admin-adv-diff-${c.id}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-amber-300 font-semibold mb-2"><GitCompare size={13} /> Resubmitted — changed since last review</div>
      <div className="space-y-1.5">
        {c.resubmit_changes.map((d, i) => (
          <div key={i} className="text-xs flex flex-wrap items-baseline gap-x-2" data-testid={`admin-adv-diff-item-${i}`}>
            <span className="text-race-txt font-semibold min-w-[110px]">{d.label}</span>
            <span className="text-race-muted line-through">{d.from}</span>
            <span className="text-race-muted">→</span>
            <span className="text-amber-200">{d.to}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
